import { CopyIcon, PeopleIcon } from '@blueprinthq/joy'
import {
  Avatar,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react'
import { MoreVertDots } from '@components/icons'
import {
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useWaitingParticipants
} from '@daily-co/daily-react'
import { useTelehealthLinks } from '@hooks'
import React from 'react'
import Panel from '../../in-session-assist-panel/panel'
import PanelHeader from '../../panel-header'
import { useActiveTelehealthSession } from '../context/active-telehealth-session.context'
import { useClinicianDailyManager } from '../context/clinician-daily-manager.context'
export const ParticipantList = ({
  user,
  onClose
}: {
  user: any
  onClose: () => void
}) => {
  const { waitingParticipants } = useWaitingParticipants()
  const participantIds = useParticipantIds()
  const localId = useLocalSessionId()
  const { admitWaitingParticipant } = useClinicianDailyManager()
  const { copyLinkToClipboard, telehealthUrlDisplay } = useTelehealthLinks()
  const { isCallOwner } = useActiveTelehealthSession()

  return (
    <Panel hideBorder={true}>
      <PanelHeader icon={<PeopleIcon />} title="People" onClose={onClose} />
      <Box p="medium">
        <Box>
          <Text fontSize="14px" fontWeight="bold">
            In the session ({participantIds.length})
          </Text>

          <Flex
            mt="xsmall"
            border="1px solid"
            borderColor="pale_gray"
            p="xsmall"
            borderRadius="12px"
            flexDirection="column"
            gap="xsmall"
          >
            {participantIds.map(participantId => (
              <ParticipantItem
                key={participantId}
                participantId={participantId}
                isLocal={participantId === localId}
                avatarUrl={
                  participantId === localId ? user?.avatar?.url : undefined
                }
              />
            ))}
          </Flex>
        </Box>

        {isCallOwner && waitingParticipants.length > 0 && (
          <Box mt="medium">
            <Text fontSize="14px" fontWeight="bold">
              In the waiting room ({waitingParticipants.length})
            </Text>

            <Flex
              mt="xsmall"
              border="1px solid"
              borderColor="pale_gray"
              p="xsmall"
              borderRadius="12px"
              flexDirection="column"
              gap="xsmall"
            >
              {waitingParticipants.map(participant => (
                <Flex
                  key={participant.id}
                  gap="xsmall"
                  alignItems="center"
                  justifyContent="space-between"
                  py="10px"
                  px="8px"
                >
                  <Flex gap="xsmall" alignItems="center">
                    <Avatar
                      name={participant.name}
                      size="sm"
                      bg="pale_gray"
                      fontWeight="bold"
                      color="black"
                    />
                    <Text ml="xxsmall">{participant.name}</Text>
                  </Flex>
                  <Button
                    borderRadius="4px"
                    variant="solid"
                    bg="primary"
                    size="sm"
                    onClick={() => {
                      admitWaitingParticipant(participant.id)
                    }}
                    data-click-text="Admit participant"
                  >
                    Admit
                  </Button>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}

        {isCallOwner && (
          <Box mt="medium">
            <Text fontSize="14px" fontWeight="bold">
              Invite people
            </Text>

            <Flex alignItems="center" mt="xsmall">
              <Flex
                style={{ border: '1px solid #E4E5E6' }}
                borderRadius={'16px'}
                width="100%"
                p="small"
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex alignItems="center">
                  <Text color="#757575">{telehealthUrlDisplay}</Text>
                </Flex>
                <IconButton
                  m="0px"
                  background={'white'}
                  _hover={{
                    bg: '#E4E5E6'
                  }}
                  size="sm"
                  aria-label="copy"
                  onClick={copyLinkToClipboard}
                  variant="outline"
                  style={{
                    borderRadius: '4px'
                  }}
                  data-click-text="Copy telehealth link"
                >
                  <CopyIcon size="sm" />
                </IconButton>
              </Flex>
            </Flex>
          </Box>
        )}
      </Box>
    </Panel>
  )
}

const ParticipantItem = ({
  participantId,
  isLocal,
  avatarUrl
}: {
  participantId: string
  isLocal: boolean
  avatarUrl?: string
}) => {
  const participantName = useParticipantProperty(participantId, 'user_name')
  const { isCallOwner } = useActiveTelehealthSession()
  const { kickParticipant } = useClinicianDailyManager()

  return (
    <Flex
      gap="xsmall"
      alignItems="center"
      justifyContent="space-between"
      py="10px"
      px="8px"
    >
      <Flex gap="xsmall" alignItems="center">
        <Avatar
          src={avatarUrl}
          name={participantName}
          size="sm"
          bg="pale_gray"
          fontWeight="bold"
          color="black"
        />
        <Text ml="xxsmall">
          {isLocal ? `${participantName} (You)` : participantName}
        </Text>
      </Flex>
      {isCallOwner && !isLocal && (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Kick participant"
            variant="outline"
            size="sm"
            minWidth="0px"
            m="0px"
            p="6px"
            borderRadius="4px"
            _hover={{
              bg: '#E4E5E6'
            }}
          >
            <MoreVertDots width="18px" />
          </MenuButton>
          <MenuList>
            <MenuItem
              onClick={() => {
                kickParticipant(participantId)
              }}
              data-click-text="Remove from session"
            >
              Remove from session
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Flex>
  )
}
